// src/pages/ResetPassword/ResetPassword.jsx
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

function validate(newPassword, confirm) {
  const issues = [];
  if (!newPassword || newPassword.length < 8) issues.push('Password must be at least 8 characters.');
  if (newPassword && !/[A-Za-z]/.test(newPassword)) issues.push('Include at least one letter.');
  if (newPassword && !/[0-9]/.test(newPassword)) issues.push('Include at least one number.');
  if (confirm !== undefined && newPassword !== confirm) issues.push('Passwords do not match.');
  return issues;
}

export default function ResetPassword() {
  const connection = process.env.REACT_APP_API_URL; // e.g. "/api" or "https://api.yoursite.com/api"
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [show, setShow] = useState(false);

  const issues = useMemo(() => validate(pw, pw2), [pw, pw2]);
  const canSubmit = token && pw && pw2 && issues.length === 0 && !busy;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setMsg(null);
    setErr(null);

    try {
      await axios.post(`${connection}/auth/reset-password`, { token, newPassword: pw });
      setMsg('Your password has been reset. You can now log in.');
      setPw('');
      setPw2('');
    } catch (e) {
      const apiMsg = e?.response?.data?.message || e.message || 'Unable to reset password. The link may be expired.';
      setErr(apiMsg);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div style={styles.wrap}>
        <h1 style={styles.h1}>Reset password</h1>
        <div style={{ ...styles.alert, ...styles.bad }}>
          Missing or invalid reset token. Please request a new link.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <h1 style={styles.h1}>Set a new password</h1>
      <p style={styles.p}>Choose a strong password you haven’t used before.</p>

      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>
          New password
          <div style={{ position: 'relative' }}>
            <input
              type={show ? 'text' : 'password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              style={styles.input}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              style={styles.toggle}
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>

        <label style={styles.label}>
          Confirm password
          <input
            type={show ? 'text' : 'password'}
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder="••••••••"
            style={styles.input}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </label>

        {issues.length > 0 && (
          <ul style={styles.list}>
            {issues.map((it, i) => (
              <li key={i} style={{ color: '#b23a2a' }}>{it}</li>
            ))}
          </ul>
        )}

        <button type="submit" style={styles.button} disabled={!canSubmit}>
          {busy ? 'Saving…' : 'Reset password'}
        </button>

        {msg && <div style={{ ...styles.alert, ...styles.ok }}>{msg}</div>}
        {err && <div style={{ ...styles.alert, ...styles.bad }}>{err}</div>}
      </form>
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 420, margin: '40px auto', padding: 20, fontFamily: '-apple-system,Roboto,Arial,sans-serif' },
  h1: { margin: '0 0 8px' },
  p: { color: '#5a6a64' },
  form: { display: 'grid', gap: 12, marginTop: 16 },
  label: { display: 'grid', gap: 6, fontSize: 14 },
  input: { padding: '10px 12px', borderRadius: 10, border: '1px solid #ccd2ce', fontSize: 16, width: '100%' },
  toggle: { position: 'absolute', right: 8, top: 8, padding: '6px 8px', border: 'none', background: '#f1f2ef', borderRadius: 8, cursor: 'pointer', fontSize: 12 },
  button: { padding: '12px 16px', borderRadius: 10, border: 'none', background: '#374845', color: '#fff', cursor: 'pointer' },
  list: { margin: '6px 0 0 18px' },
  alert: { padding: '10px 12px', borderRadius: 8, fontSize: 14, marginTop: 8 },
  ok: { background: '#e9f7ef', color: '#1e7a46' },
  bad: { background: '#fdecea', color: '#b23a2a' },
};
